import { alpha, createTheme, getContrastRatio } from '@mui/material';

const colors = {
  visits: '#3F49DB',
  notes: '#8955F2',
  rfv: '#5477D3',
  subscribers: '#4DB2FF',
  disabled: '#9E9E9E',
};

export const customPalette = {
  mode: 'light',
  primary: {
    main: '#3f49db',
  },
  secondary: {
    main: '#0F1AA5',
    light: '#6ec6ff',
    dark: '#0069c0',
    contrastText: '#fff',
  },
  positive: '#0FA459',
  negative: '#D31333',
  divider: '#b3b3b3',
  white: '#fff',
  background: { default: '#f3f3f3', main: '#fff' },
  greyStoke: '#EEEEEE',
  greyDark: '#3F3F3F',
  visits: {
    main: colors.visits,
    light: alpha(colors.visits, 0.5),
    dark: alpha(colors.visits, 0.9),
    contrastText:
      getContrastRatio(colors.visits, '#fff') > 4.5 ? '#fff' : '#111',
  },
  notes: {
    main: colors.notes,
    light: alpha(colors.notes, 0.5),
    dark: alpha(colors.notes, 0.9),
    contrastText:
      getContrastRatio(colors.notes, '#fff') > 4.5 ? '#fff' : '#111',
  },
  rfv: {
    main: colors.rfv,
    light: alpha(colors.rfv, 0.5),
    dark: alpha(colors.rfv, 0.9),
    contrastText: getContrastRatio(colors.rfv, '#fff') > 4.5 ? '#fff' : '#111',
  },
  subscribers: {
    main: colors.subscribers,
    light: alpha(colors.subscribers, 0.5),
    dark: alpha(colors.subscribers, 0.9),
    contrastText:
      getContrastRatio(colors.subscribers, '#fff') > 4.5 ? '#fff' : '#111',
  },
  disabled: {
    main: colors.disabled,
    light: alpha(colors.disabled, 0.5),
    dark: alpha(colors.disabled, 0.9),
    contrastText:
      getContrastRatio(colors.disabled, '#fff') > 4.5 ? '#fff' : '#111',
  },
};

const componentsOverrides = theme => ({
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.background.main,
        boxShadow: 'none',
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'filled',
    },
  },
  MuiTable: {
    defaultProps: {
      size: 'small',
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: 'none',
        padding: theme.spacing(1),
        '&.MuiTableCell-sizeSmall': {
          padding: theme.spacing(0.5),
        },
        '&.MuiTableCell-paddingNone': {
          padding: 0,
        },
      },
      head: {
        borderBottom: '1px solid #EEEEEE',
        color: customPalette.greyDark,
        fontWeight: 600,
        fontSize: 16,
        lineHeight: '23px',
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.background.main,
        borderRadius: '10px 10px 0px 0px!important',
        '&.MuiFilledInput-underline:before': {
          borderBottomColor: 'white!important', // Semi-transparent underline
        },
        '& input[type="date"]': {
          backgroundColor: theme.palette.white,
          borderRadius: '10px 10px 0px 0px!important',
        },
        '& input[type="month"]': {
          backgroundColor: theme.palette.white,
          borderRadius: '10px 10px 0px 0px!important',
        },
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        background: theme.palette.background.main,
      },
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '&[type=submit] ': {
          background: '#3f49db',
          borderRadius: '500px!important',
          padding: '8px 20px 8px 20px',
        },
      },
    },
  },
  RaMenuItemLink: {
    styleOverrides: {
      root: {
        borderLeft: '3px solid #fff',
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '23px',

        '&.RaMenuItemLink-active': {
          borderLeft: '3px solid ' + theme.palette.primary.main,
          backgroundColor: theme.palette.background.main,
          color: theme.palette.primary.main,
          fontWeight: 600,

          '.MuiListItemIcon-root': {
            color: theme.palette.primary.main + '!important',
          },
        },
      },
    },
  },

  //-----------------------------------------------------------
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontSize: '14px',
        lineHeight: '19px ',
        fontWeight: '600',
        letterSpacing: '0.08px',
        padding: '10px 20px',
      },
      contained: {
        borderRadius: '500px ',
        boxShadow: 'none',
        margin: '0',
        height: 'auto',
        letterSpacing: '0.8px',
      },
      outlined: {
        padding: '10px 20px',
        borderRadius: '500px ',
      },
      colorPrimary: {
        backgroundColor: '#3F49DB',
      },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        width: '24px',
        height: '24px',
        fontSize: '10px',
      },
    },
  },

  MuiTypography: {
    styleOverrides: {
      root: {
        fontFamily: 'Roboto, sans-serif',
      },
      h1: {
        fontSize: '32px',
        fontWeight: '700',
        lineHeight: '37px',
        textAlign: 'left',
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: '10px!important',
      },
    },
  },

  MuiBreadcrumbs: {
    styleOverrides: {
      root: {
        ol: {
          li: {
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '19px',
            '&:first-of-type a': {
              textDecoration: 'none',
            },

            '&:nth-of-type(2) ': {
              margin: '0 ',
            },
            '&:last-child': {
              marginLeft: '4px',
              fontWeight: '600',
            },
          },
        },
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
      },
    },
  },
});

const createCustomTheme = () => {
  const themeOptions = {
    palette: customPalette,
    shape: { borderRadius: 10 },
    sidebar: {
      width: 200,
    },
    spacing: 8,
    typography: {
      fontFamily: 'Roboto, sans-serif',
      fontSize: 14,
      h1: { fontSize: '7rem' },
      h2: { fontWeight: 400 },
      h3: { fontWeight: 500 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
    },
  };
  const theme = createTheme(themeOptions);
  theme.components = componentsOverrides(theme);
  return theme;
};

export const customTheme = createCustomTheme();
